package com.partejar.app.feature.agenda.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonSize
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.components.PartejarDockDestination
import com.partejar.app.core.ui.components.PartejarDockScaffold
import com.partejar.app.core.ui.components.PartejarSkeleton
import com.partejar.app.core.ui.components.PartejarSkeletonText
import com.partejar.app.core.ui.components.PartejarThemeToggleAction
import com.partejar.app.core.ui.components.PartejarTopBar
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.agenda.data.AgendaEvent
import com.partejar.app.feature.agenda.data.AgendaPatientDestination
import com.partejar.app.feature.agenda.data.AgendaPeriod
import com.partejar.app.feature.agenda.data.currentAgendaDateTime
import com.partejar.app.feature.agenda.data.endsAtEpochMillis
import com.partejar.app.feature.agenda.data.startsAtEpochMillis
import com.partejar.app.feature.calendar.CalendarEventDraft
import com.partejar.app.feature.calendar.CalendarEventOpenResult
import com.partejar.app.feature.calendar.rememberCalendarIntegration
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import org.koin.compose.koinInject
import com.partejar.app.core.session.CurrentWorkspaceRepository
import com.partejar.app.feature.calendar.meetingCalendarKey
import com.partejar.app.feature.calendar.presentation.DeviceCalendarSyncDialogs
import com.partejar.app.feature.calendar.presentation.rememberCalendarSyncController

@Composable
fun AgendaScreen(
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit,
    onDestinationSelected: (PartejarDockDestination) -> Unit,
    onPatientSelected: (Long, AgendaPatientDestination) -> Unit,
    viewModel: AgendaViewModel = koinInject()
) {
    val state by viewModel.state.collectAsState()
    val calendarSync = rememberCalendarSyncController()
    val workspace: CurrentWorkspaceRepository = koinInject()
    val scope = rememberCoroutineScope()
    var addingEventId by remember { mutableStateOf<Long?>(null) }
    var calendarFeedback by remember { mutableStateOf<AgendaCalendarFeedback?>(null) }
    val workspaceId = workspace.currentWorkspaceId()
    androidx.lifecycle.compose.LifecycleEventEffect(androidx.lifecycle.Lifecycle.Event.ON_RESUME) { viewModel.reload() }
    LaunchedEffect(state, workspaceId) {
        val events = (state as? AgendaUiState.Content)?.events.orEmpty()
        if (workspaceId != null) for (event in events) {
            val draft = event.calendarDraft() ?: continue
            calendarSync.submit(event.calendarKey(workspaceId), draft, suggestIfDisabled = false, reportSuccess = false)
            if (calendarSync.canRetry) break
        }
    }
    AgendaContent(
        state = state,
        isDarkTheme = isDarkTheme,
        onThemeToggle = onThemeToggle,
        onDestinationSelected = onDestinationSelected,
        onPatientSelected = onPatientSelected,
        onRetry = viewModel::reload,
        addingEventId = addingEventId,
        calendarFeedback = calendarFeedback,
        onDismissCalendarFeedback = { calendarFeedback = null },
        onAddToDeviceCalendar = { event ->
            val draft = event.calendarDraft()
            if (draft == null) {
                calendarFeedback = AgendaCalendarFeedback(
                    "Não foi possível interpretar o horário deste compromisso.",
                    PartejarAlertTone.ERROR
                )
            } else {
                addingEventId = event.id
                calendarFeedback = null
                scope.launch {
                    if (workspaceId != null) calendarSync.submit(event.calendarKey(workspaceId), draft, forceSuggestion = true)
                    addingEventId = null
                }
            }
        }
    )
    DeviceCalendarSyncDialogs(calendarSync)
}

private fun AgendaEvent.calendarKey(workspaceId: Long): String =
    if (type == "REUNIAO_APRESENTACAO" && patientId != null) meetingCalendarKey(workspaceId, patientId)
    else "$workspaceId.event.$id"

@Composable
internal fun AgendaContent(
    state: AgendaUiState,
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit,
    onDestinationSelected: (PartejarDockDestination) -> Unit,
    onPatientSelected: (Long, AgendaPatientDestination) -> Unit,
    onRetry: () -> Unit,
    addingEventId: Long? = null,
    calendarFeedback: AgendaCalendarFeedback? = null,
    onDismissCalendarFeedback: () -> Unit = {},
    onAddToDeviceCalendar: (AgendaEvent) -> Unit = {}
) {
    val spacing = PartejarTheme.spacing
    var selectedEvent by remember(state) { mutableStateOf<AgendaEvent?>(null) }
    PartejarDockScaffold(
        topBar = {
            PartejarTopBar(
                title = "Agenda",
                subtitle = "Hoje e próximos 30 dias",
                leadingContent = {
                    Box(
                        modifier = Modifier
                            .padding(start = spacing.space16)
                            .size(PartejarTheme.dimensions.minimumTouchTarget),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            imageVector = PartejarIcons.Navigation.agenda,
                            contentDescription = null
                        )
                    }
                },
                actions = { PartejarThemeToggleAction(isDarkTheme, onThemeToggle) }
            )
        }
    ) { padding, dockClearance ->
        when (state) {
            AgendaUiState.Loading -> AgendaLoading(
                modifier = Modifier.fillMaxSize().padding(padding),
                dockClearance = dockClearance
            )

            is AgendaUiState.Error -> Column(
                Modifier.fillMaxSize()
                    .padding(padding)
                    .padding(start = spacing.space16, top = spacing.space16, end = spacing.space16)
                    .padding(bottom = dockClearance + spacing.space16),
                verticalArrangement = Arrangement.spacedBy(spacing.space12)
            ) {
                PartejarAlert("Não foi possível carregar", PartejarAlertTone.ERROR) { Text(state.message) }
                PartejarButton("Tentar novamente", onRetry)
            }

            is AgendaUiState.Empty -> AgendaTimeline(
                period = state.period,
                events = emptyList(),
                dockClearance = dockClearance,
                onEventSelected = { selectedEvent = it },
                modifier = Modifier.fillMaxSize().padding(padding)
            )

            is AgendaUiState.Content -> AgendaTimeline(
                period = state.period,
                events = state.events,
                dockClearance = dockClearance,
                onEventSelected = { selectedEvent = it },
                modifier = Modifier.fillMaxSize().padding(padding)
            )
        }
    }

    selectedEvent?.let { event ->
        AgendaEventDetailSheet(
            event = event,
            addingToCalendar = addingEventId == event.id,
            calendarFeedback = calendarFeedback,
            onDismiss = {
                selectedEvent = null
                onDismissCalendarFeedback()
            },
            onOpenPatient = event.patientTarget()?.let { target ->
                {
                    selectedEvent = null
                    onPatientSelected(target.first, target.second)
                }
            },
            onAddToDeviceCalendar = { onAddToDeviceCalendar(event) }
        )
    }
}

@Composable
private fun AgendaTimeline(
    period: AgendaPeriod,
    events: List<AgendaEvent>,
    dockClearance: Dp,
    onEventSelected: (AgendaEvent) -> Unit,
    modifier: Modifier = Modifier
) {
    val spacing = PartejarTheme.spacing
    val dates = remember(period) { agendaDates(period) }
    val today = remember(period) {
        runCatching { LocalDate.parse(period.startDate) }.getOrNull() ?: currentAgendaDateTime().date
    }
    var selectedDate by remember(period) { mutableStateOf(today) }
    var pendingScrollDate by remember { mutableStateOf<LocalDate?>(null) }
    val groups = remember(events, selectedDate) { agendaDayGroups(events, selectedDate) }
    val groupStartIndices = remember(groups) {
        buildMap {
            var index = 0
            groups.forEach { group ->
                put(group.date, index)
                index += 1 + group.events.size.coerceAtLeast(1)
            }
        }
    }
    val listState = rememberLazyListState()
    val nextEventId = remember(events) { nextAgendaEventId(events, currentAgendaDateTime()) }

    LaunchedEffect(pendingScrollDate, groups) {
        pendingScrollDate?.let { date ->
            groupStartIndices[date]?.let { listState.animateScrollToItem(it) }
            pendingScrollDate = null
        }
    }
    LaunchedEffect(listState, groupStartIndices) {
        snapshotFlow { listState.firstVisibleItemIndex }
            .distinctUntilChanged()
            .collect { index ->
                if (pendingScrollDate == null) {
                    groupStartIndices.entries.lastOrNull { it.value <= index }?.key?.let { selectedDate = it }
                }
            }
    }

    Column(modifier) {
        AgendaDateStrip(
            dates = dates,
            selectedDate = selectedDate,
            today = today,
            onDateSelected = { date ->
                selectedDate = date
                pendingScrollDate = date
            }
        )
        LazyColumn(
            modifier = Modifier.fillMaxSize(),
            state = listState,
            contentPadding = PaddingValues(
                start = spacing.space16,
                top = spacing.space16,
                end = spacing.space16,
                bottom = dockClearance + spacing.space16
            ),
            verticalArrangement = Arrangement.spacedBy(spacing.space8)
        ) {
            groups.forEach { group ->
                item(key = "day-${group.date}", contentType = "day-header") {
                    AgendaDayHeader(group.date, today)
                }
                if (group.events.isEmpty()) {
                    item(key = "empty-${group.date}", contentType = "day-empty") {
                        AgendaDayEmpty(group.date == today)
                    }
                } else {
                    items(group.events, key = { "event-${it.id}" }, contentType = { "event" }) { event ->
                        AgendaEventRow(
                            event = event,
                            highlighted = event.id == nextEventId,
                            onClick = { onEventSelected(event) }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun AgendaDateStrip(
    dates: List<LocalDate>,
    selectedDate: LocalDate,
    today: LocalDate,
    onDateSelected: (LocalDate) -> Unit
) {
    if (dates.isEmpty()) return
    val spacing = PartejarTheme.spacing
    val stripState = rememberLazyListState()
    LaunchedEffect(selectedDate, dates) {
        val index = dates.indexOf(selectedDate)
        if (index >= 0) stripState.animateScrollToItem(index)
    }
    Column(
        modifier = Modifier.fillMaxWidth()
            .padding(horizontal = spacing.space16)
            .padding(top = spacing.space8),
        verticalArrangement = Arrangement.spacedBy(spacing.space8)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                modifier = Modifier.weight(1f),
                horizontalArrangement = Arrangement.spacedBy(spacing.space8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    PartejarIcons.Clinical.calendar,
                    contentDescription = null,
                    modifier = Modifier.size(PartejarTheme.dimensions.iconSmall),
                    tint = MaterialTheme.colorScheme.primary
                )
                Text(
                    agendaMonthHeading(selectedDate),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            if (selectedDate != today) {
                PartejarButton(
                    text = "Hoje",
                    onClick = { onDateSelected(today) },
                    variant = PartejarButtonVariant.TERTIARY,
                    size = PartejarButtonSize.COMPACT,
                    fillWidth = false
                )
            }
        }
        LazyRow(
            modifier = Modifier.fillMaxWidth().selectableGroup(),
            state = stripState,
            horizontalArrangement = Arrangement.spacedBy(spacing.space8),
            contentPadding = PaddingValues(end = spacing.space16)
        ) {
            items(dates, key = { it.toString() }) { date ->
                AgendaDateItem(
                    date = date,
                    selected = date == selectedDate,
                    today = today,
                    onClick = { onDateSelected(date) }
                )
            }
        }
    }
}

@Composable
private fun AgendaDateItem(
    date: LocalDate,
    selected: Boolean,
    today: LocalDate,
    onClick: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    Surface(
        modifier = Modifier
            .widthIn(min = spacing.space48)
            .heightIn(min = spacing.space64)
            .selectable(selected = selected, onClick = onClick, role = Role.Tab)
            .semantics {
                this.selected = selected
                contentDescription = agendaDayHeading(date, today)
            },
        shape = PartejarShapes.radius20,
        color = if (selected) MaterialTheme.colorScheme.primaryContainer else Color.Transparent,
        contentColor = if (selected) MaterialTheme.colorScheme.onPrimaryContainer else MaterialTheme.colorScheme.onSurface,
        border = if (selected) null else BorderStroke(dimensions.strokeThin, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(
            modifier = Modifier.padding(horizontal = spacing.space8, vertical = spacing.space8),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Text(date.dayOfMonth.toString(), style = MaterialTheme.typography.titleSmall)
            Text(
                agendaWeekdayShort(date.dayOfWeek),
                style = MaterialTheme.typography.labelSmall,
                color = if (selected) MaterialTheme.colorScheme.onPrimaryContainer else MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun AgendaDayHeader(date: LocalDate, today: LocalDate) {
    val spacing = PartejarTheme.spacing
    Column(
        modifier = Modifier.fillMaxWidth()
            .padding(top = spacing.space8)
            .semantics { heading() },
        verticalArrangement = Arrangement.spacedBy(spacing.space8)
    ) {
        Text(agendaDayHeading(date, today), style = MaterialTheme.typography.titleMedium)
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
    }
}

@Composable
private fun AgendaEventRow(
    event: AgendaEvent,
    highlighted: Boolean,
    onClick: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    Surface(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth().heightIn(min = spacing.space64),
        shape = PartejarShapes.radius14,
        color = if (highlighted) MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.66f) else Color.Transparent,
        contentColor = MaterialTheme.colorScheme.onSurface
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(vertical = spacing.space12, horizontal = spacing.space8),
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.Top
        ) {
            Column(
                modifier = Modifier.width(spacing.space48),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(spacing.space4)
            ) {
                Icon(
                    agendaEventIcon(event.type),
                    contentDescription = null,
                    modifier = Modifier.size(dimensions.iconSmall),
                    tint = if (highlighted) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant
                )
                Text(
                    agendaEventTime(event),
                    style = MaterialTheme.typography.titleSmall,
                    color = if (highlighted) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurface,
                    fontWeight = FontWeight.Bold
                )
            }
            Box(
                Modifier.width(dimensions.strokeStrong)
                    .heightIn(min = spacing.space48)
                    .align(Alignment.CenterVertically)
            ) {
                Surface(
                    modifier = Modifier.fillMaxSize(),
                    color = if (highlighted) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outlineVariant,
                    shape = PartejarShapes.pill
                ) {}
            }
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(spacing.space4)
            ) {
                if (highlighted) {
                    Text("PRÓXIMO", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary)
                }
                Text(
                    event.title,
                    style = MaterialTheme.typography.labelLarge,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
                event.patientName?.let {
                    AgendaEventMetadata(
                        icon = PartejarIcons.Navigation.gestantes,
                        text = it,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
                agendaEventType(event.type)?.takeIf { it != event.title }?.let {
                    AgendaEventMetadata(
                        icon = PartejarIcons.Clinical.calendar,
                        text = it,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
            }
            Icon(
                PartejarIcons.Actions.next,
                contentDescription = "Abrir detalhes",
                modifier = Modifier.size(dimensions.iconMedium),
                tint = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun AgendaEventMetadata(
    icon: ImageVector,
    text: String,
    style: TextStyle
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space4),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            icon,
            contentDescription = null,
            modifier = Modifier.size(PartejarTheme.dimensions.iconSmall),
            tint = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text,
            modifier = Modifier.weight(1f, fill = false),
            style = style,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun AgendaDayEmpty(isToday: Boolean) {
    val spacing = PartejarTheme.spacing
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = spacing.space16),
        horizontalArrangement = Arrangement.spacedBy(spacing.space8),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            PartejarIcons.Clinical.calendar,
            contentDescription = null,
            modifier = Modifier.size(PartejarTheme.dimensions.iconMedium),
            tint = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            if (isToday) "Nenhum compromisso para hoje." else "Nenhum compromisso neste dia.",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}

@Composable
@OptIn(ExperimentalMaterial3Api::class)
private fun AgendaEventDetailSheet(
    event: AgendaEvent,
    addingToCalendar: Boolean,
    calendarFeedback: AgendaCalendarFeedback?,
    onDismiss: () -> Unit,
    onOpenPatient: (() -> Unit)?,
    onAddToDeviceCalendar: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    ModalBottomSheet(onDismissRequest = onDismiss, sheetState = sheetState) {
        Column(
            modifier = Modifier.fillMaxWidth()
                .navigationBarsPadding()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = spacing.space20)
                .padding(bottom = spacing.space24),
            verticalArrangement = Arrangement.spacedBy(spacing.space16)
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(spacing.space12),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Surface(
                    modifier = Modifier.size(spacing.space48),
                    shape = PartejarShapes.pill,
                    color = MaterialTheme.colorScheme.primaryContainer,
                    contentColor = MaterialTheme.colorScheme.primary
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Icon(agendaEventIcon(event.type), contentDescription = null)
                    }
                }
                Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                    Text(event.title, style = MaterialTheme.typography.titleLarge)
                    Text(
                        agendaEventTimeRange(event),
                        style = MaterialTheme.typography.titleSmall,
                        color = MaterialTheme.colorScheme.primary
                    )
                }
            }
            agendaEventType(event.type)?.takeIf { it != event.title }?.let {
                DetailLine(PartejarIcons.Clinical.calendar, "Tipo", it)
            }
            event.patientName?.let { DetailLine(PartejarIcons.Navigation.gestantes, "Gestante", it) }
            calendarFeedback?.let {
                PartejarAlert("Agenda do dispositivo", it.tone) { Text(it.message) }
            }
            onOpenPatient?.let {
                PartejarButton(
                    text = if (event.patientDestination == AgendaPatientDestination.GESTANTE) "Abrir gestante" else "Abrir relacionamento",
                    onClick = it,
                    modifier = Modifier.fillMaxWidth(),
                    variant = PartejarButtonVariant.SECONDARY,
                    leadingIcon = {
                        Icon(
                            if (event.patientDestination == AgendaPatientDestination.GESTANTE) {
                                PartejarIcons.Navigation.gestantes
                            } else {
                                PartejarIcons.Navigation.relacionamentos
                            },
                            contentDescription = null
                        )
                    }
                )
            }
            PartejarButton(
                text = "Adicionar à agenda do celular",
                onClick = onAddToDeviceCalendar,
                modifier = Modifier.fillMaxWidth(),
                isLoading = addingToCalendar,
                enabled = !addingToCalendar,
                leadingIcon = { Icon(PartejarIcons.Clinical.calendar, contentDescription = null) }
            )
            Text(
                "Você poderá revisar os dados antes de salvar no calendário do dispositivo.",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun DetailLine(icon: ImageVector, label: String, value: String) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            icon,
            contentDescription = null,
            modifier = Modifier.size(PartejarTheme.dimensions.iconMedium),
            tint = MaterialTheme.colorScheme.primary
        )
        Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space4)) {
            Text(label, style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            Text(value, style = MaterialTheme.typography.bodyLarge)
        }
    }
}

private fun agendaEventIcon(type: String): ImageVector = when (type) {
    "REUNIAO_APRESENTACAO" -> PartejarIcons.Relationships.meeting
    "OFICINA_PREPARACAO" -> PartejarIcons.Navigation.prontuario
    "OFICINA_AMAMENTACAO" -> PartejarIcons.Relationships.prenatal
    "OFICINA_CUIDADOS_RN" -> PartejarIcons.Relationships.baby
    "OUTRO" -> PartejarIcons.Relationships.other
    else -> PartejarIcons.Clinical.calendar
}

@Composable
private fun AgendaLoading(modifier: Modifier, dockClearance: Dp) {
    val spacing = PartejarTheme.spacing
    Column(modifier) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = spacing.space16, vertical = spacing.space12),
            horizontalArrangement = Arrangement.spacedBy(spacing.space8)
        ) {
            repeat(5) { PartejarSkeleton(Modifier.width(spacing.space48).height(spacing.space64), PartejarShapes.radius20) }
        }
        LazyColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(
                start = spacing.space16,
                end = spacing.space16,
                top = spacing.space12,
                bottom = dockClearance + spacing.space16
            ),
            verticalArrangement = Arrangement.spacedBy(spacing.space16)
        ) {
            items(4) {
                Row(horizontalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    PartejarSkeletonText(Modifier.width(spacing.space48).height(spacing.space20))
                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.spacedBy(spacing.space8)
                    ) {
                        PartejarSkeletonText(Modifier.fillMaxWidth(0.72f).height(spacing.space16))
                        PartejarSkeletonText(Modifier.fillMaxWidth(0.46f).height(spacing.space12))
                    }
                }
            }
        }
    }
}

internal data class AgendaCalendarFeedback(
    val message: String,
    val tone: PartejarAlertTone
)

private fun AgendaEvent.patientTarget(): Pair<Long, AgendaPatientDestination>? =
    patientId?.let { id -> patientDestination?.let { destination -> id to destination } }

private fun AgendaEvent.calendarDraft(): CalendarEventDraft? {
    val start = startsAtEpochMillis() ?: return null
    val end = endsAtEpochMillis() ?: return null
    val context = listOfNotNull(
        patientName?.let { "Gestante: $it" },
        agendaEventType(type)?.takeIf { it != title }
    ).joinToString("\n").ifBlank { null }
    return CalendarEventDraft(
        title = title,
        startsAtEpochMillis = start,
        endsAtEpochMillis = end,
        description = context
    )
}

private fun CalendarEventOpenResult.feedback(): AgendaCalendarFeedback? = when (this) {
    CalendarEventOpenResult.OPENED -> AgendaCalendarFeedback(
        "Editor aberto. Revise os dados antes de salvar.",
        PartejarAlertTone.SUCCESS
    )
    CalendarEventOpenResult.PERMISSION_DENIED -> AgendaCalendarFeedback(
        "O dispositivo não permitiu abrir o calendário.",
        PartejarAlertTone.ATTENTION
    )
    CalendarEventOpenResult.UNAVAILABLE -> AgendaCalendarFeedback(
        "Nenhum calendário compatível está disponível neste dispositivo.",
        PartejarAlertTone.INFO
    )
    CalendarEventOpenResult.FAILED -> AgendaCalendarFeedback(
        "Não foi possível abrir o calendário do dispositivo.",
        PartejarAlertTone.ERROR
    )
}
